import React, { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeProps } from '@styles/theme';
import { useTheme } from '@mui/material/styles';

import VRIcon from '@assets/vr.svg';
import ARIcon from '@assets/ar.svg';
import MobileIcon from '@assets/tablet.svg';
import PCIcon from '@assets/pc.svg';
import AndroidIcon from '@assets/android.svg';
import IOSIcon from '@assets/ios.svg';
import GenericAR from '@assets/ar.svg';
import _ from 'lodash';
import classNames from 'classnames';
import { Tooltip } from '@mui/material';
import color from 'color';
import DeviceUnknownIcon from '@mui/icons-material/DeviceUnknown';


type DeviceIconProps = {
    id?: number;
    platform: string;
    environments?: string | null;
    size?: 'small' | 'normal';
    showLabel?: boolean;
}

const useStyles = createUseStyles((theme: ThemeProps) => ({
    deviceIcon: {
        paddingRight: theme.spacing(1),
    },
    deviceIconSmall: {
        maxWidth: '28px',
        maxHeight: '28px',
    },
}))

export const DeviceIcon: FC<DeviceIconProps> = props => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const { id, platform, environments, size, showLabel = true } = props;

    const deviceTypesImages = {
        'VR': VRIcon,
        'AR': ARIcon,
        'Mobile': MobileIcon,
        'PC': PCIcon,
        'pc': PCIcon,
        'pc-vr': VRIcon,
        'windows': PCIcon,
        'windows-screen2d': PCIcon,
        'windows-vr': VRIcon,
        'osx': PCIcon,
        'osx-screen2d': PCIcon,
        'osx-vr': VRIcon,
        'android': AndroidIcon,
        'android-screen2d': AndroidIcon,
        'android-ar': ARIcon,
        'android-vr': VRIcon,
        'android-mr': VRIcon,
        'ios': IOSIcon,
        'ios-screen2d': IOSIcon,
        'ios-ar': ARIcon,
        'ios-vr': VRIcon,
        'ios-mr': VRIcon,
        'visionos-vr': VRIcon,
        'visionos-mr': VRIcon,
        'visionos-ar': VRIcon,
        'wsa': GenericAR,
    } as { [key: string]: string };

    // remove the 'screen2d' suffix from the environment
    const getIconData = (platform: string, environment: string | null) => {
        const environmentSuffix = environment?.toLowerCase().replace('screen2d', '');
        const key = platform.toLowerCase() + (environmentSuffix ? '-' + environmentSuffix : '');
        const title = platform + (environmentSuffix ? ' ' + environment : '');
        const imgSrc = deviceTypesImages[key] ?? deviceTypesImages[platform] ?? DeviceUnknownIcon;

        return { imgSrc, title, key: id + key };
    }

    return (
        <div style={{
            background: `${color(theme.colors.violet_primary).fade(0.92)}`,
            padding: '5px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '5px',
            marginRight: '5px',
        }}>
            {_.uniqBy(environments?.split('|').map(env => getIconData(platform, env)), v => v.imgSrc).map(({ imgSrc, title, key }) =>
                <Tooltip title={title} key={imgSrc} arrow>
                    <img
                        className={classNames(styles.deviceIcon, { [styles.deviceIconSmall]: size === 'small' })}
                        src={imgSrc}
                        alt={platform ?? ''}
                        key={key}
                    />
                </Tooltip>
            )}
            {showLabel && (
                <p style={{
                    alignContent: 'center',
                    margin: 0,
                }}>{platform}</p>
            )}
        </div>
    )
}
